import type { InternalAxiosRequestConfig } from 'axios'
import axios, { AxiosRequestConfig } from 'axios'
import config, { sviPrefix } from '@/config'
import { message } from 'antd'
import {
  getRefreshToken,
  getTenantId,
  getToken,
  getTokenExpire,
  onLogout,
  resolveResponse,
  saveToken,
} from '@/common/utils'
import { refreshTokenApi, X_TRANSFER_KEY } from '@/services'
import { router } from '@/main'
import global from '@/utils/global.ts'
import UrlConfig from '@/api/UrlConfigs.ts'
import { matchUrl } from '@/utils/common.ts'

export let controller = new AbortController()

// 禁用请求和无token情况下，仍然可以请求的url集合
export const noTokenAllowReqList = [
  '/platform-web/config', // 平台配置
  '/caas-web/wxControlPanel/loginByCode',
  '/caas-web/controlPanel/grantAuthorization', // 租户登录
  '/platform-web/controlPanel/grantAuthorization', // 平台、租户登录
  '/caas-web/lostFound/sendVerificationCode', // 修改密码 发送验证码
  UrlConfig.Caas.SaasQrCodeLogin, //  扫码登录
  '/caas-web/lostFound/foundPassword', // 重置密码
  '/sys/getPasswordKey',
  '/sys/auth',
  UrlConfig.Caas.resetPasswordWithRequireChangePassword, // 初次登录 修改密码
  UrlConfig.Caas.resetPasswordWithSendCode, // 忘记密码 发送验证码
  UrlConfig.UserWeb.UcenterControlPanelAuth, // 登录获取授权
  UrlConfig.Caas.UcenterControlPanelAuthCode, // 登录
  UrlConfig.Caas.resetPasswordWithCheckCode,
  `${UrlConfig.Caas.GrantAuthorizationByAuthCode}/{userId}`, // 登录
  `${UrlConfig.Caas.SendLoginVerifyCode}/{phone}`, // 获取验证码
  UrlConfig.Caas.UcenterControlResetPassword,
  UrlConfig.Caas.getLoginParams, // 获取登录参数
]

export function disabledRequest() {
  // sessionStorage.setItem('requestAbort', 'true')
  global.isAbortingRequest = true
}

export function enableRequest() {
  // sessionStorage.removeItem('requestAbort')
  global.isAbortingRequest = false
}

export function abortRequest(delay = 100) {
  controller.abort()
  disabledRequest()
  return new Promise(resolve => {
    setTimeout(() => {
      controller = new AbortController()
      resolve(controller)
    }, delay)
  })
}

export enum METHODS {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

export const baseURL = config.api
// export const sviURL = config.svi
export const sviURL = config.svi2

export const requestHelper = {
  microDict: {},
  bind(opts) {
    if (opts.name) {
      requestHelper.microDict[opts.name] = {
        logout: opts.logout,
      }
    }
  },
}

// console.log('baseURL', baseURL)

const api = axios.create({
  baseURL,
  timeout: 60000,
})
let reqQueue: any[] = []
const errQueue: any[] = []
let isRefreshing = false
// let isGettingXTenantId = false
// let XTenantReqQueue: any[] = []

const routerLogin = (config?) => {
  onLogout()

  const { logout } = requestHelper.microDict[config?.microName] || {}
  if (logout) {
    logout?.()
  } else {
    abortRequest(0).then(() => router.navigate('/login', { replace: true }))
  }
}

// 只调用 刷新token接口 和 保存token
export const sendRefreshToken = async () => {
  const refreshToken = getRefreshToken()
  return refreshTokenApi(refreshToken).then((res: any) => {
    const { accessToken } = res || {}
    saveToken(accessToken.token)
    localStorage.setItem('expireAt', accessToken.expireAt)
    return accessToken.token
  })
}
export const getRefreshTokenApi = config => {
  const token: string = getToken() || ''
  const tokenExpire = getTokenExpire() || ''
  const refreshToken = getRefreshToken()
  if (token && refreshToken && tokenExpire && config.url !== UrlConfig.Caas.RefreshTokenApi) {
    const now = Date.now()
    // if (now >= Date.parse(tokenExpireDate) - 5000) {
    if (now >= parseInt(tokenExpire) - 5000) {
      if (!isRefreshing) {
        isRefreshing = true
        sessionStorage.setItem('IS_REFRESHING_TOKEN', '1')
        sendRefreshToken()
          .then((newToken: string) => {
            reqQueue.forEach(cb => cb(newToken))
            reqQueue = []
            isRefreshing = false
            sessionStorage.removeItem('IS_REFRESHING_TOKEN')
            // return res
          })
          .catch((err: any) => {
            if (err?.response?.data?.status === 404) {
              routerLogin(config)
            }
            errQueue.forEach(cb => cb(err))
            reqQueue = []
          })
      }
      if (isRefreshing && config) {
        // console.log('refreshing', config)
        return new Promise((resolve, reject) => {
          reqQueue.push((newToken: string) => {
            // delete config.defaults.headers.common['Authorization']
            config.headers['Authorization'] = newToken
            config.headers['x-transfer-inside-tenant-id'] = getTenantId()
            return resolve(config)
          })
          errQueue.push(reject)
        })
        // throw new Error('token is refreshing')
      }
    }
  }
}

// 添加请求拦截器
api.interceptors.request.use(
  (config): InternalAxiosRequestConfig<any> | Promise<any> => {
    // console.log(config.url, noTokenAllowReqList)
    const isAllow = noTokenAllowReqList.find(url => {
      return url === config.url || matchUrl(url, config.url)?.match
    })
    if (!isAllow && !getToken() && global.isAbortingRequest) {
      return Promise.reject('no token is disabled request, consider add api to noTokenAllowReqList')
    }

    if (config.headers && !config['noExtraHeader']) {
      const xTenantId = getTenantId()

      try {
        const refreshConfig = getRefreshTokenApi(config)
        if (refreshConfig) {
          return refreshConfig
        }
      } catch (e) {
        return Promise.reject(e)
      }

      if (xTenantId) {
        config.headers[X_TRANSFER_KEY] = config.headers[X_TRANSFER_KEY] || xTenantId
      }

      if (localStorage.getItem('token')) {
        // config.headers['Authorization'] = localStorage.getItem('token');

        // config.headers['TOKEN'] = localStorage.getItem('token');
        if (!config.headers['x-tenant-id']) {
          let tenantId = ''
          if (config.data?.tenantId) {
            tenantId = config.data.tenantId
          }
          if (!tenantId && config.params?.tenantId) {
            tenantId = config.params.tenantId
          }
          if (tenantId) {
            config.headers['x-tenant-id'] = tenantId
            config.headers['X-OTP'] = tenantId
          }
        }
        const SVIToken = localStorage.getItem('token') || ''
        if (SVIToken && config.headers?.Authorization !== false) {
          try {
            config.headers['Authorization'] = JSON.parse(SVIToken)
          } catch (err) {
            console.log('err', err)
          }
        }
      }

      config.signal = config.signal || controller.signal
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 添加响应拦截器
api.interceptors.response.use(
  (response: any) => {
    if (response.status === 200) {
      const code = response?.data?.code ?? 0
      if (code == 401 || code == 801) {
        routerLogin(response.config)
      } else if ((response?.data?.errorCode ?? 0) === 0) {
        return resolveResponse(response)
      }
    }
    if (response?.config?.error !== false) {
      message.error(`${response?.data?.message || '服务器异常,请重试!'}`)
    }
    return Promise.reject(response?.data)
  },
  error => {
    if (process.env.NODE_ENV === 'development') {
      // console.log('err', error)
    }
    if (error && error.response) {
      const { config, response } = error || {}

      if (response?.data?.errorCode === 401 && !config?.url?.includes(sviPrefix)) {
        routerLogin(config)
      }
      if (config.error !== false) {
        message.error(
          `${
            response.data?.message ||
            response.data?.error ||
            response.status + ' ' + errorMap[response.status] ||
            '服务器异常,请重试!'
          }`
        )
      }
    } else if (error.code === 'ERR_NETWORK') {
      message.error('网络异常')
    }
    return Promise.reject(error?.response?.data ?? error)
  }
)

const errorMap = {
  500: '服务器异常',
  404: '接口不存在',
  401: '访问未授权',
}

type RequestConfig = {
  error?: boolean //显示错误信息
  raw?: boolean
  microName?: string // 区分应用 独立配置
  noExtraHeader?: boolean
} & Omit<AxiosRequestConfig, 'url'>

function request<T>(url: string, option: RequestConfig): Promise<T> {
  return api.request({
    url,
    ...option,
  })
}

export const sviRequest = (url: string, option?: RequestConfig) => {
  return api.request(url, {baseURL: sviURL, ...option})
}

export default request
